import { useState } from "react";
import styled from "styled-components";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import ChipInput from "material-ui-chip-input";
import { Alert } from "@material-ui/lab";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ButtonWithLoading from "components/ButtonWithLoading";
import WithIconHelper from "components/WithIconHelper";
import SyncTimeSelector from "./SyncTimeSelector";
import { createSource, updateSource } from "../api";
import { useSourcesContext } from "../sourcesProvider";
import { LdapSettings } from "../types";
import ApiError from "utils/ApiError";
import { SourceConfiguration, SyncScheduleTime } from "@dashboard-v3/api";
import SwitchField from "components/Forms/SwitchField";

interface Props {
  id?: string;
  isOpen: boolean;
  settings?: LdapSettings;
  onClose: () => void;
}
export default function LdapSourceDialog(props: Props) {
  const { isOpen, onClose, settings, id } = props;
  const { t, actions } = useSourcesContext();
  const [ldap, setLdap] = useState<LdapSettings>(settings || defaultLdapData);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleDialogClose = () => {
    onClose();
    setErrorMsg(null);
  };

  const isEditing = () => !!id;

  const updateField = event => {
    const { name, value } = event.target;
    setLdap(old => ({ ...old, [name]: value }));
  };

  const updateSwitch = event => {
    const { name, checked } = event.target;
    setLdap(old => ({
      ...old,
      [name]: checked,
    }));
  };

  const updateLdap = async () => {
    const { syncScheduleTime, ...ldapSettings } = ldap;
    const body: SourceConfiguration = {
      type: "LDAP",
      syncScheduleTime,
      ldapSettings,
    };

    try {
      const source = await updateSource(id, body);
      setIsSaving(false);
      actions.updateSource(source);
      onClose();
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.statusCode === 504) {
        setErrorMsg(t("error.updateSourceTimeout"));
      } else {
        setErrorMsg(t("error.createSource"));
      }
      setIsSaving(false);
    }
  };

  const createLdap = async () => {
    const { syncScheduleTime, ...ldapSettings } = ldap;
    const body: SourceConfiguration = {
      type: "LDAP",
      syncScheduleTime,
      ldapSettings,
    };

    try {
      const source = await createSource(body);
      setIsSaving(false);
      actions.addSource(source);
      onClose();
    } catch (error) {
      //@ts-ignore
      console.error(`Error Code ${error.code} with status ${error.status}`);
      //@ts-ignore
      if (error.code === "TIMEOUT" || error.status === 504) {
        setErrorMsg(t("error.createSourceTimeout"));
      } else {
        setErrorMsg(t("error.createSource"));
      }
      setIsSaving(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setErrorMsg(null);
    setIsSaving(true);
    return isEditing() ? updateLdap() : createLdap();
  };

  const title = () => {
    const key = isEditing()
      ? "sources.editTypeOfSource"
      : "sources.addTypeOfSource";
    return t(key, { type: "LDAP" });
  };

  const handleSyncScheduleTime = (syncTime: SyncScheduleTime | null) => {
    setLdap(prev => {
      const update = { ...prev };

      if (syncTime === null) {
        delete update.syncScheduleTime;
      } else {
        update.syncScheduleTime = syncTime;
      }
      return update;
    });
  };

  const handleAdditionalMailAttrs = (values: string[]) => {
    setLdap(prev => ({
      ...prev,
      additionalMailAttributes: values,
    }));
  };

  return (
    <Dialog
      open={isOpen}
      onClick={e => e.stopPropagation()}
      onClose={handleDialogClose}
      scroll="paper"
    >
      <DialogTitle>
        {title()}
        {errorMsg && (
          <Alert elevation={0} variant="filled" severity="error">
            {errorMsg}
          </Alert>
        )}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <StyledTextField
            name="label"
            label={t("ldap.label")}
            value={ldap.label}
            onChange={updateField}
            variant="outlined"
            fullWidth
            inputProps={{
              "data-testid": "accounts__source-ldap-label",
            }}
          />
          <Box mb="16px">
            <TextField
              name="hostAddress"
              label={t("ldap.hostAddress")}
              value={ldap.hostAddress}
              onChange={updateField}
              required
              variant="outlined"
              fullWidth
              inputProps={{
                "data-testid": "accounts__source-address",
              }}
            />
          </Box>
          <StyledTextField
            name="portNumber"
            label={t("ldap.portNumber")}
            type="number"
            value={ldap.portNumber}
            onChange={updateField}
            required
            variant="outlined"
            fullWidth
            inputProps={{
              "data-testid": "accounts__source-port",
            }}
          />
          <SwitchField
            name="secure"
            label={t("ldap.secure")}
            data-testid="accounts__use-ssl-switch"
            value={ldap.secure}
            onChange={updateSwitch}
            style={{ marginBottom: 20 }}
          />
          <StyledTextField
            name="userDn"
            label={t("ldap.userDn")}
            value={ldap.userDn}
            onChange={updateField}
            required
            variant="outlined"
            fullWidth
            inputProps={{
              "data-testid": "accounts__source-user",
            }}
          />
          <StyledTextField
            name="password"
            label={t("ldap.password")}
            type="password"
            value={ldap.password}
            onChange={updateField}
            required
            variant="outlined"
            fullWidth
            inputProps={{
              "data-testid": "accounts__source-password",
            }}
          />
          <StyledTextField
            name="usersFilter"
            label={t("ldap.usersFilter")}
            value={ldap.usersFilter}
            onChange={updateField}
            multiline
            variant="outlined"
            fullWidth
            inputProps={{
              "data-testid": "accounts__source-user-filter",
            }}
          />
          <StyledTextField
            name="groupsFilter"
            label={t("ldap.groupsFilter")}
            value={ldap.groupsFilter}
            onChange={updateField}
            multiline
            variant="outlined"
            fullWidth
            inputProps={{
              "data-testid": "accounts__source-group-filter",
            }}
          />
          <StyledTextField
            name="base"
            label={t("ldap.base")}
            value={ldap.base}
            onChange={updateField}
            variant="outlined"
            fullWidth
            inputProps={{
              "data-testid": "accounts__source-base-filter",
            }}
          />
          <WithIconHelper
            position="after"
            text={t("common:chipInputHelper")}
            icon={<InfoOutlinedIcon fontSize="small" color="primary" />}
            buttonStyles={{ marginLeft: "10px" }}
          >
            <ChipInput
              label={t("ldap.additionalMailAttributes")}
              defaultValue={ldap.additionalMailAttributes}
              onChange={handleAdditionalMailAttrs}
              variant="outlined"
              color="primary"
              fullWidth
              data-testid="accounts__source-mail-attributes"
            />
          </WithIconHelper>
          <div style={{ marginTop: 20 }}>
            <SyncTimeSelector
              variant="outlined"
              value={ldap.syncScheduleTime}
              onChange={handleSyncScheduleTime}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogClose}
            data-testid="accounts__source-cancel-edit"
          >
            {t("common:cancel")}
          </Button>
          <ButtonWithLoading
            type="submit"
            loading={isSaving}
            variant="contained"
            color="primary"
            disableElevation
            data-testid="accounts__save-source-btn"
          >
            {t("common:save")}
          </ButtonWithLoading>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const StyledTextField = styled(TextField)`
  margin-bottom: 16px;
`;

const defaultLdapData = {
  label: "",
  hostAddress: "",
  portNumber: 389,
  secure: false,
  userDn: "",
  password: "",
  usersFilter:
    "(&(|(objectclass=user)(objectclass=person)(objectclass=inetOrgPerson) (objectclass=organizationalPerson))(!(objectclass=computer))(|(mail=*)(userPrincipalName=*)))",
  groupsFilter:
    "(|(objectclass=group)(objectclass=groupofnames)(objectclass=groupofuniquenames))",
  base: "",
  additionalMailAttributes: [
    "mailAlternateAddress",
    "zimbraMailAlias",
    "proxyAddresses",
  ],
};
